<template>
  <BaseModal
    :modal-id="modalId"
    :title="title"
    :cancel-text="cancelText"
    :confirm-text="confirmText"
    :header-style="headerStyle"
    v-model="internalVisible"
    @cancel="handleCancel"
    @confirm="handleConfirm"
  >
    <div class="text-modal-content">
      <!-- メインメッセージ -->
      <p v-if="message" class="main-message">
        {{ message }}
      </p>

      <!-- 詳細テキスト（複数行対応） -->
      <div v-if="details" class="details-text">
        <p v-for="(line, index) in detailLines" :key="index" class="detail-line">
          {{ line }}
        </p>
      </div>

      <!-- HTMLコンテンツ（v-htmlで表示、DOMPurifyでサニタイズ済み） -->
      <div v-if="htmlContent" class="html-content" v-html="sanitizedHtmlContent">
      </div>

      <!-- スロットでカスタムコンテンツも可能 -->
      <div v-if="$slots.default" class="custom-content">
        <slot></slot>
      </div>
    </div>
  </BaseModal>
</template>

<script setup>
import { computed } from 'vue'
import DOMPurify from 'dompurify'
import BaseModal from './BaseModal.vue'

/**
 * TextModal - テキスト表示用モーダルコンポーネント (Molecule)
 *
 * BaseModalを拡張してテキスト表示に特化したモーダルコンポーネントです。
 * シンプルなメッセージから複数行の詳細テキスト、HTMLコンテンツまで
 * 柔軟に表示できる汎用的なテキストモーダルを提供します。
 *
 * アトミックデザイン構造:
 * - Molecule: TextModal (このコンポーネント)
 *   └── Molecule: BaseModal
 *     └── Bootstrap Vue: b-modal, b-button
 *
 * 責務:
 * - テキストコンテンツの表示
 * - メッセージ、詳細テキスト、HTMLコンテンツの表示
 * - v-modelによるモーダル表示制御
 * - ユーザーアクションの親コンポーネント‌への通知
 * - スロットによるカスタムコンテンツ対応
 *
 * 使用例:
 * <TextModal
 *   v-model="showModal"
 *   title="確認"
 *   message="この操作を実行しますか？"
 *   details="この操作は取り消すことができません。\n続行する場合はOKをクリックしてください。"
 *   header-style="gradient"
 *   @confirm="handleConfirm"
 *   @cancel="handleCancel"
 * />
 */

const props = defineProps({
  /**
   * モーダルのユニークID
   */
  modalId: {
    type: String,
    default: 'text-modal'
  },

  /**
   * モーダルのタイトル
   */
  title: {
    type: String,
    required: true
  },

  /**
   * メインメッセージ
   */
  message: {
    type: String,
    default: ''
  },

  /**
   * 詳細テキスト（改行文字で複数行に分割される）
   */
  details: {
    type: String,
    default: ''
  },

  /**
   * HTMLコンテンツ（v-htmlで表示）
   * 注意: XSS攻撃を防ぐため、信頼できるコンテンツのみ使用
   */
  htmlContent: {
    type: String,
    default: ''
  },

  /**
   * キャンセルボタンのテキスト
   */
  cancelText: {
    type: String,
    default: 'キャンセル'
  },

  /**
   * 確認ボタンのテキスト
   */
  confirmText: {
    type: String,
    default: 'OK'
  },

  /**
   * モーダルの表示状態（Vue 2互換）
   */
  value: {
    type: Boolean,
    default: false
  },

  /**
   * モーダルの表示状態（Vue 3 v-model）
   */
  modelValue: {
    type: Boolean,
    default: false
  },

  /**
   * ヘッダーのスタイル
   * - 'default': 標準のヘッダースタイル
   * - 'gradient': グラデーションヘッダー
   * - 'dark': ダークヘッダー
   */
  headerStyle: {
    type: String,
    default: 'default',
    validator: (value) => ['default', 'gradient', 'dark'].includes(value)
  }
})

const emit = defineEmits(['input', 'update:modelValue', 'cancel', 'confirm'])

/**
 * 詳細テキストを改行で分割した配列
 */
const detailLines = computed(() => {
  if (!props.details) return []
  return props.details.split('\n').filter(line => line.trim() !== '')
})

/**
 * XSS攻撃を防ぐためDOMPurifyでサニタイズしたコンテンツ
 */
const sanitizedHtmlContent = computed(() => {
  if (!props.htmlContent) return ''
  return DOMPurify.sanitize(props.htmlContent)
})

/**
 * 内部的なモーダル表示状態
 * v-modelとの双方向バインディング用
 */
const internalVisible = computed({
  get: () => props.modelValue !== undefined ? props.modelValue : props.value,
  set: (visible) => {
    // Vue 2互換のinputイベント
    emit('input', visible)
    // Vue 3のv-modelイベント
    emit('update:modelValue', visible)
  }
})

/**
 * キャンセルボタンクリック時の処理
 */
const handleCancel = () => {
  /**
   * @event cancel
   */
  emit('cancel')
  // モーダルを閉じる
  internalVisible.value = false
}

/**
 * 確認ボタンクリック時の処理
 */
const handleConfirm = () => {
  /**
   * @event confirm
   */
  emit('confirm')
  // モーダルを閉じる
  internalVisible.value = false
}
</script>

<style lang="scss" scoped>
.text-modal-content {
  text-align: left;
  line-height: 1.6;
}

// メインメッセージのスタイル
.main-message {
  font-size: 1rem;
  font-weight: 500;
  margin-bottom: 1rem;
  color: #374151;
}

// 詳細テキストのスタイル
.details-text {
  margin-bottom: 1rem;

  .detail-line {
    font-size: 0.9rem;
    color: #6b7280;
    margin-bottom: 0.5rem;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

// HTMLコンテンツのスタイル
.html-content {
  margin-bottom: 1rem;

  // HTMLコンテンツ内の要素のスタイル調整
  :deep(h1), :deep(h2), :deep(h3), :deep(h4), :deep(h5), :deep(h6) {
    margin-top: 0;
    margin-bottom: 0.5rem;
    color: #374151;
  }

  :deep(p) {
    margin-bottom: 0.75rem;
    color: #6b7280;

    &:last-child {
      margin-bottom: 0;
    }
  }

  :deep(ul), :deep(ol) {
    margin-bottom: 0.75rem;
    padding-left: 1.5rem;
  }

  :deep(li) {
    margin-bottom: 0.25rem;
    color: #6b7280;
  }

  :deep(code) {
    background-color: #f3f4f6;
    padding: 0.125rem 0.25rem;
    border-radius: 0.25rem;
    font-family: 'Courier New', monospace;
    font-size: 0.875rem;
  }

  :deep(pre) {
    background-color: #f3f4f6;
    padding: 0.75rem;
    border-radius: 0.375rem;
    overflow-x: auto;
    margin-bottom: 0.75rem;

    code {
      background-color: transparent;
      padding: 0;
    }
  }
}

// カスタムコンテンツのスタイル
.custom-content {
  margin-bottom: 1rem;
}

// 複数のコンテンツがある場合の間隔調整
.main-message + .details-text,
.main-message + .html-content,
.main-message + .custom-content,
.details-text + .html-content,
.details-text + .custom-content,
.html-content + .custom-content {
  margin-top: 0.75rem;
}

// レスポンシブ対応
@media (max-width: 768px) {
  .text-modal-content {
    font-size: 0.9rem;
  }

  .main-message {
    font-size: 0.95rem;
  }

  .details-text .detail-line {
    font-size: 0.85rem;
  }
}
</style>
